import { Account } from '../account.domain';
import { HoldingsResponse } from '../holding.domain';
import { Currency, MarketType } from '../market.domain';
import { SecuritySymbol, SymbolType } from '../symbol.domain';
import { AssetCategoryGroupType, AssetCategoryType } from './asset-allocation.domain';
import { InvestmentGoal, LtiAccount } from './lti.domain';
import { RebalancePeriod, RebalanceType, PortfolioAllocationStatus, ProfileModelType, AssetStatus } from './investment-portfolio.enum';

export interface Allocation {
  targetAllocationPct?: number;
  currentAllocationPct?: number;
  targetAllocationAmt?: number;
  currentAllocationAmt?: number;
  cartAllocationAmt?: number;
  cartAllocationPct?: number;
  effectiveCurrentAllocationPct?: number; // percentage shown in pie chart when currentAllocationPct is below 0
}

export interface AssetAllocation extends Allocation {
  assetCategoryType?: AssetCategoryType;
  status?: AssetAllocationStatus;
  assetCategoryGroupType?: AssetCategoryGroupType;
}

export interface ExchangeTradedFund extends ExchangeTradedFundResponse {
  symbolName: string;
  description: string;
  market: MarketType;
}

export interface ExchangeTradedFundResponse {
  symbolType: SymbolType;
  investmentProductId: string;
  subtypeCd: SymbolType;
}

export interface InvestorProfileModel {
  templateProfileModelId?: string;
  selected?: boolean;
  bestYearReturnPct?: number;
  worstYearReturnPct?: number;
  averageYearReturnPct?: number;
  annualPerformanceRiskPct?: number;
  riskLevel?: number;
  assetCategoryGroupAllocations?: Array<AssetAllocation>;
  profileModelId?: string;
  profileModelType: ProfileModelType;
  portfolioAllocationStatus?: PortfolioAllocationStatus;
  assetAllocations?: Array<AssetAllocation>;
  exchangeTradedFunds?: Array<ExchangeTradedFundResponse>;
}

export interface InvestmentPortfolioAllocation {
  goal: InvestmentGoal;
  model: InvestorProfileModel;
  accounts: Record<string, PortfolioAccountHoldings>;
}

export interface PortfolioAccountHoldings {
  account: Account;
  records: Array<HoldingsResponse>;
}

export class InvestorProfileModelHelper {
  static isEqualAllocation(profile1: InvestorProfileModel, profile2: InvestorProfileModel): boolean {
    let result = profile1 !== undefined && profile2 !== undefined && profile1.profileModelType === profile2.profileModelType;
    if (result === true && profile1 !== null) {
      result = !profile1.assetAllocations.some((allocation1: AssetAllocation): boolean => {
        const allocation2 = profile2.assetAllocations
          .find((allocation: AssetAllocation): boolean => allocation.assetCategoryType === allocation1.assetCategoryType);
        return allocation2 === undefined || allocation2.targetAllocationPct !== allocation1.targetAllocationPct;
      });
    }
    return result;
  }

  static isEqualAllocationAndBenchmarks(profile1: InvestorProfileModel, profile2: InvestorProfileModel): boolean {
    let result = this.isEqualAllocation(profile1, profile2);
    if (result) {
      result = profile1.riskLevel === profile2.riskLevel &&
        profile1.bestYearReturnPct === profile2.bestYearReturnPct &&
        profile1.worstYearReturnPct === profile2.worstYearReturnPct &&
        profile1.averageYearReturnPct === profile2.averageYearReturnPct &&
        profile1.annualPerformanceRiskPct === profile2.annualPerformanceRiskPct;
    }

    return result;
  }
}

export interface InvestmentPortfolio {
  goalId: string;
  investmentPortfolioId?: string;
  rebalancePeriod?: RebalancePeriod;
  rebalanceType?: RebalanceType;
  investmentAccounts?: Array<LtiAccount>;
}

export interface AssetAllocationStatus {
  status: AssetStatus;
  pctDeviance: number;
  amtDeviance: number;
}

export interface InvestmentProductDetail {
  investmentProductId: string;
  symbol: SecuritySymbol;
  assetAllocations: Array<AssetAllocation>;
  riskLevel: number;
  closePrice: number;
  closePriceCurrency: Currency;
}

export interface BasicHolding {
  name: string;
  percentOfAccount: number;
}

export enum  PortfolioPerformancePeriodType {
  LAST_MONTH = 'LAST_MONTH',
  LAST_3_MONTHS = 'LAST_3_MONTHS',
  LAST_12_MONTHS = 'LAST_12_MONTHS',
  LAST_3_YEARS = 'LAST_3_YEARS',
  LAST_10_YEARS = 'LAST_10_YEARS',
  SINCE_INCEPTION = 'SINCE_INCEPTION'
}

export type PortfolioPerformanceType = Record<PortfolioPerformancePeriodType, number>;

export interface ProductPerformanceDetail {
  portfolioPerformance: PortfolioPerformanceType;
  asOfDate: Date;
  inceptionDate: Date;
}

export interface EtfProductDetail extends InvestmentProductDetail {
  fundObjective: string;
  performanceDetail: ProductPerformanceDetail;
  topHoldings: Array<BasicHolding>;
  managementExpenseRatio: number;
  managementExpenseRatioAsOfDate: Date;
  dividendYield: number;
  dividendYieldAsOfDate: Date;
  numberOfHoldings: number;
  assetsUnderManagement: number;
  fundAssetsNetTotalAmt: number;
  benchmark: string;
  portfolioAdvisorGroup: string;
}

